// Scalar date funcs — `to-date`, `date-bucket`. Usable in any
// expression context (filter predicates, derive bodies, top-level
// bindings). Replace common math.js-era date helpers from the
// pre-s-expr transform pipeline.

import { z } from 'zod';

import { TypeMismatchError } from '../../errors.ts';
import type { CompileFn, RawOpCall, RowFn } from '../../op-meta.ts';
import { opMeta } from '../../op-meta.ts';
import { decodeSymbol, SymEnum } from '../../schema/sym-codec.ts';
import { type ColumnType, stringCol, timestampCol } from '../../schema/types.ts';
import type { Value } from '../../value.ts';
import { bucketTimestamp, formatDateValue, type Period, Periods, toDateValue } from './date-utils.ts';

/**
 * `(to-date <string|number>)` — parse to a `Date` scalar. Returns
 * `null` if the input is null/invalid. ISO-8601 strings, epoch
 * milliseconds, and `Date` values pass through.
 */
export const toDate = z
  .object({
    name: z.literal('to-date'),
    params: z.tuple([z.unknown()]),
  })
  .describe('Parse a string or epoch-ms number to a Date. Returns null on invalid input.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(to-date "2024-01-01")', result: 'Date' },
    apply: ({ params: [v] }): Value => toDateValue(v) as Value,
    compileExpr: (call: RawOpCall, compile: CompileFn, path: string): RowFn => {
      const arg = compile((call.params ?? [])[0], `${path}/params/0`);
      return (row) => toDateValue(arg(row));
    },
    inferType: (): ColumnType => timestampCol,
  });

/**
 * `(date-bucket ts <period>)` — bucket a timestamp to the start of
 * its period using UTC. Returns `null` on invalid input.
 */
export const dateBucket = z
  .object({
    name: z.literal('date-bucket'),
    params: z.tuple([z.unknown(), SymEnum(Periods)]),
  })
  .describe('Bucket a timestamp to the start of a period (minute / hour / day / week / month / quarter / year).')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(date-bucket ts "week")' },
    apply: ({ params: [v, period] }): Value => bucketTimestamp(v, period as Period) as Value,
    compileExpr: (call: RawOpCall, compile: CompileFn, path: string): RowFn => {
      const tsArg = compile((call.params ?? [])[0], `${path}/params/0`);
      // Raw AST here (compileExpr bypasses the schema codec): the
      // period is either a bare symbol `{$sym:'week'}` or a quoted
      // string `'week'`. `decodeSymbol` normalises both to the bare
      // member — the exact decode the `SymEnum` schema codec applies.
      const periodArg = decodeSymbol((call.params ?? [])[1]) as Period;
      if (!(Periods as readonly string[]).includes(periodArg)) {
        throw new TypeMismatchError(`:period to be one of ${Periods.join(' | ')}`, String(periodArg), 'date-bucket', {
          line: 0,
          column: 0,
          offset: 0,
          length: 0,
        } as never);
      }
      return (row) => bucketTimestamp(tsArg(row), periodArg);
    },
    inferType: (): ColumnType => timestampCol,
  });

/**
 * `(format-date <string|number|Date> [pattern])` — format a value to
 * a display string in UTC mode. Inverse of `(to-date …)`. Accepts the
 * same inputs as `to-date` (ISO-8601 string, epoch-ms number, Date).
 * Optional positional dayjs pattern (default `"YYYY-MM-DD"`). Returns
 * `null` for null / unparseable input. UTC (not the display
 * timezone) so engine output is deterministic.
 */
export const formatDate = z
  .object({
    name: z.literal('format-date'),
    params: z.array(z.unknown()).min(1).max(2),
  })
  .describe(
    'Format a date/epoch-ms/ISO value to a string in UTC. Optional dayjs pattern (default "YYYY-MM-DD"). Null/invalid ⇒ null.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(format-date "2024-01-15T12:34:00Z" "YYYY/MM/DD")', result: '"2024/01/15"' },
    apply: ({ params: [v, pattern] }): Value => formatDateValue(v, pattern as string | undefined),
    compileExpr: (call: RawOpCall, compile: CompileFn, path: string): RowFn => {
      const vArg = compile((call.params ?? [])[0], `${path}/params/0`);
      const rawPattern = (call.params ?? [])[1];
      const patternArg = rawPattern === undefined ? undefined : compile(rawPattern, `${path}/params/1`);
      return (row) => formatDateValue(vArg(row), patternArg === undefined ? undefined : (patternArg(row) as string));
    },
    inferType: (): ColumnType => stringCol,
  });
